import React from "react";
import { PropTypes } from "prop-types";
import { Card, Button, Row, Col } from "react-bootstrap";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { BiPlusMedical, BiTrash } from "react-icons/bi";
import { FaRegEye } from "react-icons/fa";

import AppBreadcrumb from "views/Admin/Layout/AppBreadCrumb";
import PiDataTable from "views/DataTable/PiDataTable";

import {
  getCredentialsList,
  resetComponentStore,
  deleteCredential,
} from "actions/credentialActions";

import CredentialFilters from "./CredentialFilters";

const CredentialsList = ({
  credentialList: { data, count },
  getCredentialsList,
  loadingCredentialList,
  resetComponentStore,
  sortingParams,
  deleteCredential,
}) => {
  const navigate = useNavigate();

  const initialSortingParams = {
    limit: 20,
    page: 1,
    orderBy: "createdAt",
    ascending: "desc",
    query: "",
    filters: [],
  };

  const { page, limit } = sortingParams;

  const [clientParams, setClientParams] = React.useState(initialSortingParams);
  const [onlyOnce, setOnce] = React.useState(true);

  const actions = (
    <div className="page-actions">
      <Link to="/admin/credentials/create" title="create">
        <Button color="primary" size="sm">
          <BiPlusMedical /> Create New Credential
        </Button>
      </Link>
    </div>
  );

  const columns = [
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: false,
      width: "20%",
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: false,
      width: "40%",
      wrap: true,
    },
    {
      name: "Actions",
      width: "calc(40% - 48px)",
      button: true,
      cell: (row) => (
        <div className="App table-list-buttons">
          <Link
            to={`/admin/credentials/${row._id}/edit`}
            title="View Credential"
          >
            <Button variant="primary" size="sm">
              <FaRegEye />
            </Button>
          </Link>

          <Button
            className="ml-1"
            size="sm"
            type="button"
            variant="danger"
            onClick={(e) => {
              if (
                window.confirm(`Are you sure you want to delete: ${row.code}?`)
              ) {
                deleteCredential(row._id);
              }
            }}
          >
            <BiTrash />
          </Button>
        </div>
      ),
    },
  ];

  React.useMemo(() => {
    if (onlyOnce) {
      resetComponentStore();
      setOnce(false);
    }

    getCredentialsList(clientParams);
  }, [getCredentialsList, clientParams, resetComponentStore]);

  const handleTableChange = (type, searchText) => {
    let params = {
      limit: limit,
      page: type === "search" ? 1 : page ? page : 1,
    };

    let filters = [];
    if (type === "search") {
      if (searchText.length > 0) {
        filters = sortingParams.filters.includes(type)
          ? sortingParams.filters
          : [...sortingParams.filters, type];
        params = {
          ...params,
          query: {
            ...sortingParams.query,
            [type]: {
              code: {
                value: searchText,
                type: "String",
              },
              description: {
                value: searchText,
                type: "String",
              },
            },
          },
          filters,
        };
      } else {
        filters = sortingParams.filters.filter((item) => item !== type);
        const temp = {};
        params = {
          ...sortingParams,
          filters,
        };
        for (var i in params.query) {
          if (i === type) continue;
          temp[i] = params.query[i];
        }
        params.query = temp;
      }
    }

    setClientParams(params);
  };

  const onFilterChange = (newParams) => {
    setClientParams((params) => ({ ...params, ...newParams }));
  };

  return (
    <React.Fragment>
      <AppBreadcrumb
        pageTitle="Credentials"
        crumbs={[{ name: "Credentials" }]}
      />

      <Card>
        <Card.Body>
          {actions}

          <div className="table-filter-section">
            <Row>
              <Col md="2">
                <CredentialFilters
                  type="text"
                  onSearch={handleTableChange}
                  filterType="String"
                  filterParams={clientParams}
                  onFilterChange={onFilterChange}
                />
              </Col>
            </Row>
          </div>

          <PiDataTable
            columns={columns}
            data={data}
            count={count}
            params={clientParams}
            setParams={setClientParams}
            pagination
            responsive
            striped={true}
            selectableRows
            progressPending={loadingCredentialList}
            highlightOnHover
            persistTableHead={true}
            paginationServer
          />
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

CredentialsList.propTypes = {
  getCredentialsList: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  credentialList: state.credential.credentialList,
  loadingCredentialList: state.credential.loadingCredentialList,
  sortingParams: state.credential.sortingParams,
});

export default connect(mapStateToProps, {
  getCredentialsList,
  resetComponentStore,
  deleteCredential,
})(CredentialsList);
